import React from 'react';
import {
  Modal,
  Autocomplete,
  Text,
  Loader,
  Box,
  Flex,
  Avatar,
  Title,
} from '@cowbell/ui';
import { useSearchUsers, UserSearchResult } from '../hooks/useSearchUsers';
import { constructAvatarInitials } from '../../../components/notes/AvatarWithInitialsFallBack';
import { StaffPhotoAvatar } from '../../../components/user-avatar/StaffPhotoAvatar';
import classes from './AssignUserModal.module.css';

type AssignUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUserSelect: (value: string | UserSearchResult | null) => void;
  searchTerm: string;
  inputValue: string;
  onSearchTermChange: (term: string) => void;
  onInputValueChange: (value: string) => void;
  title?: string;
};

export const AssignUserModal = ({
  isOpen,
  onClose,
  onUserSelect,
  searchTerm,
  inputValue,
  onSearchTermChange,
  onInputValueChange,
  title = 'Assign Email To',
}: AssignUserModalProps) => {
  const usersSearchQuery = useSearchUsers(searchTerm, isOpen);
  const users = usersSearchQuery.data?.users || [];

  const handleInputChange = (newValue: string) => {
    onInputValueChange(newValue);
    onSearchTermChange(newValue);
  };

  const getOptionLabel = (user: UserSearchResult) =>
    `${user.firstName || ''} ${user.lastName || ''} (${user.email})`.trim();

  const handleOptionSubmit = (selectedLabel: string) => {
    const selectedUser = users.find(
      (user) => getOptionLabel(user) === selectedLabel
    );
    onUserSelect(selectedUser || selectedLabel);
  };

  const autocompleteData = users.map((userOption) => ({
    value: getOptionLabel(userOption),
    label: getOptionLabel(userOption),
    userId: userOption.id,
    firstName: userOption.firstName,
    lastName: userOption.lastName,
    email: userOption.email,
    roleName: userOption.role?.name,
  }));

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Title order={4}>{title}</Title>}
      size="md"
      centered
      zIndex={9999}
      styles={{ inner: { position: 'fixed', inset: 0 } }}
    >
      <Autocomplete
        placeholder="Search users by name or email..."
        value={inputValue}
        onChange={handleInputChange}
        onOptionSubmit={handleOptionSubmit}
        data={autocompleteData}
        filter={() => autocompleteData}
        autoFocus
        comboboxProps={{ zIndex: 10000 }}
        rightSection={usersSearchQuery.isLoading ? <Loader size={16} /> : null}
        renderOption={({ option }) => {
          const optionData = option as any;
          return (
            <Flex align="center" gap="sm">
              <StaffPhotoAvatar
                email={optionData.email}
                name={`${optionData.firstName || ''} ${
                  optionData.lastName || ''
                }`.trim()}
                size="lg"
              >
                <Avatar size={40}>
                  {constructAvatarInitials(
                    `${optionData.firstName || ''} ${optionData.lastName || ''}`
                  )}
                </Avatar>
              </StaffPhotoAvatar>
              <Box>
                <Text size="sm">
                  {optionData.firstName} {optionData.lastName}
                </Text>
                <Text size="xs" c="dimmed">
                  {optionData.email}
                </Text>
                {optionData.roleName && (
                  <Text size="xs" c="dimmed">
                    {optionData.roleName}
                  </Text>
                )}
              </Box>
            </Flex>
          );
        }}
      />

      <Text size="xs" c="dimmed" className={classes.hintText}>
        Type at least 2 characters to search. Press ESC to cancel.
      </Text>
    </Modal>
  );
};
